import React, { useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { COLORS } from '@/lib/colors';
import { ApexOptions } from 'apexcharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Props {
  universities: Array<{
    university: string;
    national_rank: number;
    grad_rate: number;
    retention_rate: number;
    peer_assessment: number;
    faculty_resources_rank: number;
    financial_resources_rank: number;
    [key: string]: any;
  }>;
}

const CATEGORY_OPTIONS = [
  { value: 'grad_rate', label: 'Graduation Rate', isInverted: false },
  { value: 'retention_rate', label: 'Retention Rate', isInverted: false },
  { value: 'peer_assessment', label: 'Peer Assessment', isInverted: false },
  { value: 'faculty_resources_rank', label: 'Faculty Resources', isInverted: true },
  { value: 'financial_resources_rank', label: 'Financial Resources', isInverted: true },
];

export function TopPerformersChart({ universities }: Props) {
  const [selectedCategory, setSelectedCategory] = useState('peer_assessment');
  const selectedOption = CATEGORY_OPTIONS.find(opt => opt.value === selectedCategory);

  const data = useMemo(() => {
    const sortedUniversities = [...universities].sort((a, b) => {
      if (selectedOption?.isInverted) {
        return a[selectedCategory] - b[selectedCategory];
      }
      return b[selectedCategory] - a[selectedCategory];
    });

    return sortedUniversities.slice(0, 10).map(uni => ({
      name: uni.university,
      value: uni[selectedCategory],
      rank: uni.national_rank,
      isDepaul: uni.university === 'DePaul University'
    }));
  }, [universities, selectedCategory, selectedOption]);

  const options: ApexOptions = {
    chart: {
      type: 'bar' as const,
      fontFamily: 'inherit',
      toolbar: {
        show: false
      },
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '70%',
        distributed: true,
        dataLabels: {
          position: 'top'
        }
      }
    },
    colors: data.map(item => 
      item.isDepaul 
        ? COLORS.accent.orange 
        : [
            COLORS.primary.darkBlue,
            COLORS.primary.mediumBlue,
            COLORS.primary.lightBlue,
            COLORS.supporting.turquoise,
            COLORS.supporting.mint
          ][Math.floor(Math.random() * 5)]
    ),
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return selectedOption?.isInverted ? `#${val}` : val.toFixed(1);
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        fontWeight: 600,
        colors: [COLORS.text.primary]
      }
    },
    xaxis: {
      categories: data.map(item => item.name),
      labels: {
        style: {
          colors: data.map(item => item.isDepaul ? COLORS.accent.orange : COLORS.text.primary),
          fontSize: '12px'
        },
        rotate: -45,
        rotateAlways: true,
        trim: false,
        maxHeight: 100
      },
      title: {
        text: 'Universities',
        style: {
          fontSize: '12px',
          fontWeight: 500,
          color: COLORS.text.primary
        }
      }
    },
    yaxis: {
      title: {
        text: selectedOption?.label,
        style: {
          fontSize: '12px',
          fontWeight: 500,
          color: COLORS.text.primary
        }
      },
      labels: {
        formatter: function(val: number) {
          return selectedOption?.isInverted ? `#${val}` : val.toFixed(1);
        },
        style: {
          colors: COLORS.text.primary
        }
      }
    },
    tooltip: {
      custom: function({ series, seriesIndex, dataPointIndex, w }: any) {
        const item = data[dataPointIndex];
        return `
          <div class="p-4 bg-white rounded-lg shadow-lg border">
            <div class="font-medium" style="color: ${item.isDepaul ? COLORS.accent.orange : COLORS.text.primary}">${item.name}</div>
            <div style="color: ${COLORS.text.secondary}">National Rank: #${item.rank}</div>
            <div style="color: ${COLORS.text.secondary}">${selectedOption?.label}: ${
              selectedOption?.isInverted ? `#${item.value}` : item.value.toFixed(1)
            }</div>
          </div>
        `;
      }
    },
    grid: {
      borderColor: COLORS.border.light,
      strokeDashArray: 3,
      padding: {
        bottom: 100 // Add padding for rotated labels
      }
    },
    theme: {
      mode: 'light'
    }
  };

  const series = [{
    name: selectedOption?.label,
    data: data.map(item => item.value)
  }];

  return (
    <div className="space-y-4">
      <Select value={selectedCategory} onValueChange={setSelectedCategory}>
        <SelectTrigger className="w-full border-[#4FAADD]/20 bg-white focus:ring-[#274791]/20">
          <SelectValue placeholder="Select Category" />
        </SelectTrigger>
        <SelectContent>
          {CATEGORY_OPTIONS.map(option => (
            <SelectItem 
              key={option.value} 
              value={option.value}
              className="hover:bg-[#274791]/5 focus:bg-[#274791]/5 focus:text-[#274791]"
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="w-full h-[500px]">
        <ReactApexChart
          options={options}
          series={series}
          type="bar"
          height={500}
        />
      </div>
    </div>
  );
}
